import type Phaser from "phaser";

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

let dispatchLogEl: HTMLDivElement | null = null;
let commsLogEl: HTMLDivElement | null = null;
let phaserGame: Phaser.Game | null = null;
let unreadCount = 0;
let radioElapsedSec = 0;

function emitUnread(): void {
  phaserGame?.events.emit("ccc_radio_dispatch_unread", { count: unreadCount });
}

/** Investigation clock (gameworld) for radio line prefixes. */
export function setRadioElapsedSec(elapsedSec: number): void {
  if (typeof elapsedSec !== "number" || !Number.isFinite(elapsedSec)) return;
  radioElapsedSec = Math.max(0, Math.floor(elapsedSec));
}

/** Bracketed gameworld timestamp, e.g. [00:00] or [1:05:09]. */
export function formatRadioTimestamp(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `[${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}]`;
  }
  return `[${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}]`;
}

function formatRadioLineHtml(
  speaker: string,
  body: string,
  elapsedSec = radioElapsedSec,
  className?: string
): string {
  const stamp = formatRadioTimestamp(elapsedSec);
  const who = escapeHtml(speaker.trim() || "Detective");
  const msg = escapeHtml(body.trim());
  const cls = className ? ` class="${className}"` : "";
  return `<div${cls}><span class="radio-line-meta">${stamp} ${who}:</span> ${msg}</div>`;
}

/** Wire dispatch log element and Phaser game for unread events. */
export function initRadioDispatch(dispatchLog: HTMLDivElement, game: Phaser.Game | null): void {
  dispatchLogEl = dispatchLog;
  phaserGame = game;
}

/** Optional comms log (Radio Comms tab). */
export function initRadioCommsLog(commsLog: HTMLDivElement): void {
  commsLogEl = commsLog;
}

export function getDispatchUnreadCount(): number {
  return unreadCount;
}

export function clearDispatchUnread(): void {
  if (unreadCount === 0) return;
  unreadCount = 0;
  emitUnread();
}

function incrementDispatchUnread(): void {
  unreadCount += 1;
  emitUnread();
}

/** Append a baby-blue Dispatch line to the dispatch log. */
export function appendDispatchMessage(text: string, elapsedSec?: number): void {
  const body = text.trim();
  if (!body) return;
  if (dispatchLogEl) {
    dispatchLogEl.innerHTML += formatRadioLineHtml(
      "Dispatch",
      body,
      elapsedSec,
      "radio-dispatch"
    );
    dispatchLogEl.scrollTop = dispatchLogEl.scrollHeight;
  }
  incrementDispatchUnread();
}

/** Player radio chat line (Radio Comms tab). */
export function appendRadioCommsMessage(
  displayName: string,
  text: string,
  elapsedSec?: number
): void {
  const label = displayName.trim() || "Detective";
  const body = text.trim();
  if (!body || !commsLogEl) return;
  commsLogEl.innerHTML += formatRadioLineHtml(label, body, elapsedSec, "radio-comms");
  commsLogEl.scrollTop = commsLogEl.scrollHeight;
}

/** Clue-discovery copy aligned with toast headline + clue name. */
export function formatClueDiscoveryDispatch(
  discovererName: string,
  clueName: string
): string {
  const who = discovererName.trim() || "A detective";
  const clue = clueName.trim() || "new evidence";
  return `${who} has discovered a new clue — ${clue}`;
}

export function resetRadioDispatch(): void {
  unreadCount = 0;
  radioElapsedSec = 0;
  if (dispatchLogEl) dispatchLogEl.innerHTML = "";
  if (commsLogEl) commsLogEl.innerHTML = "";
  emitUnread();
}
